import { IAPICodeDTO } from '$/types'
import { IColorByValueAttributeSegmentColorProfile } from 'app/services/api/colorByValue'

import { getGradientSamplesFromHexCodes } from 'features/DataManagement/Components/ColorByValue/utils/cbvThemes'

export const generateColorRangeForBooleanType = (
  hexCodes: string[]
): IColorByValueAttributeSegmentColorProfile[] => {
  const colorArray = getGradientSamplesFromHexCodes(hexCodes, 2)
  const firstHex = colorArray[0]
  const lastHex = colorArray.at(-1)!

  return [
    {
      color: firstHex,
      value: {
        boolean: false
      }
    },
    {
      color: lastHex,
      value: {
        boolean: true
      }
    }
  ]
}

export const generateColorRangeForNumberType = (
  hexCodes: string[],
  lowerBound: number,
  upperBound: number,
  rangeCount = 5,
  isYearValue = false
): IColorByValueAttributeSegmentColorProfile[] => {
  const colorArray = getGradientSamplesFromHexCodes(hexCodes, rangeCount)
  const upperCeil = Math.ceil(upperBound)
  const lowerFloor = Math.floor(lowerBound)
  const difference = Math.abs(upperCeil - lowerFloor)
  let interval = rangeCount

  // NOTE: Here we try to snap to int values if difference is big enough
  // of if it's a year value
  if (difference >= rangeCount || isYearValue) {
    interval = Math.ceil(difference / rangeCount)
  } else {
    interval = difference / rangeCount
  }

  const res: IColorByValueAttributeSegmentColorProfile[] = []

  for (let i = 0; i < rangeCount; i++) {
    res.push({
      color: colorArray[i],
      value: {
        number: {
          min: lowerFloor + interval * i,
          max: lowerFloor + interval * (i + 1)
        }
      }
    })
  }

  return res
}

export const generateColorRangeForDateType = (
  hexCodes: string[],
  lowerBound: string,
  upperBound: string,
  rangeCount = 5
): IColorByValueAttributeSegmentColorProfile[] => {
  const lowerDateSplit = lowerBound.split('-')
  const upperDateSplit = upperBound.split('-')

  const lowerYearBound = +lowerDateSplit[0]
  const lowerMonthBound = +lowerDateSplit[1]
  const lowerDayBound = +lowerDateSplit[2]

  const upperYearBound = +upperDateSplit[0]
  const upperMonthBound = +upperDateSplit[1]
  const upperDayBound = +upperDateSplit[2]

  let lowerDateBound = new Date(
    Date.UTC(lowerYearBound, lowerMonthBound - 1, lowerDayBound)
  )
  let upperDateBound = new Date(
    Date.UTC(upperYearBound, upperMonthBound - 1, upperDayBound)
  )

  let normalizedLowerBound = lowerBound
  let normalizedUpperBound = upperBound

  if (lowerDateBound > upperDateBound) {
    ;[lowerDateBound, upperDateBound] = [upperDateBound, lowerDateBound]
    ;[normalizedLowerBound, normalizedUpperBound] = [upperBound, lowerBound]
  }

  const differenceInMilliseconds =
    upperDateBound.getTime() - lowerDateBound.getTime()

  const millisecondsInADay = 1000 * 60 * 60 * 24
  const differenceInDays = differenceInMilliseconds / millisecondsInADay

  let interval = differenceInDays / rangeCount

  if (differenceInDays < rangeCount) {
    interval = 1
    rangeCount = differenceInDays ? differenceInDays : 1
  }

  const colorArray = getGradientSamplesFromHexCodes(hexCodes, rangeCount)
  const res: IColorByValueAttributeSegmentColorProfile[] = []
  const upperBoundTimestamp = upperDateBound.getTime()

  for (let i = 0; i < rangeCount; i++) {
    const minDate = new Date(
      Math.min(
        lowerDateBound.getTime() + interval * millisecondsInADay * i,
        upperBoundTimestamp
      )
    )

    const maxDate = new Date(
      Math.min(
        lowerDateBound.getTime() + interval * millisecondsInADay * (i + 1),
        upperBoundTimestamp
      )
    )

    res.push({
      color: colorArray[i],
      value: {
        date: {
          minDate:
            i === 0
              ? normalizedLowerBound
              : minDate.toISOString().split('T')[0],
          maxDate:
            i === rangeCount - 1
              ? normalizedUpperBound
              : maxDate.toISOString().split('T')[0]
        }
      }
    })
  }

  return res
}

export const generateColorRangeForStringType = (
  hexCodes: string[],
  sets: IAPICodeDTO[]
): IColorByValueAttributeSegmentColorProfile[] => {
  const rangeCount = sets.length
  const colorArray = getGradientSamplesFromHexCodes(hexCodes, rangeCount)

  const res: IColorByValueAttributeSegmentColorProfile[] = sets.map(
    (val, idx) => ({
      color: colorArray[idx],
      value: {
        string: val
      }
    })
  )

  return res
}
